import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { Spinner } from "@/shared/components/spinner";
import { DicomViewer } from "@/features/patient/diagnostics/components/imaging/DicomViewer";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useImagingStudies } from "@/portals/patient/context/ImagingStudiesContext";
import {
  getImagingStudy,
  getSeriesGroupId,
  ImagingStudiesApiError,
  type ImagingSeries,
} from "@/portals/patient/api/imagingStudiesApi";

const DIAGNOSTICS_PATH = "/patient/diagnostics";

/**
 * DICOM viewer — nested under PatientLayout like every other page (see
 * router.tsx), so it keeps the normal sidebar/header; not a sidebar nav
 * destination itself, reached only by clicking a study in Recent DICOM
 * Uploads or right after a successful upload (see DiagnosticsImagingView.tsx's
 * openViewer). Conceptually replaces the Diagnostics & Imaging page's own
 * content area — same page background/padding convention as that page, and
 * PageBackButton (the same shared back-button used elsewhere, e.g.
 * RecordDetails.tsx) always returns to it directly.
 *
 * Content is the same DicomViewer used before this was a modal — toolbar,
 * canvas, frame scrubber, brightness bars.
 */
export function DicomViewerPage() {
  const { studyId } = useParams<{ studyId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const { getCachedFileBlob, cacheFileBlob } = useImagingStudies();

  // Passed straight through from whichever click navigated here (a Recent
  // DICOM Uploads row, or the imaging list) — both already have the full
  // series object (every member file, in order) in hand, so reusing it here
  // skips a redundant fetch. `studyId` in the URL is the series' first
  // member's id (see RecentImagingUploadsCard/DiagnosticsImagingView's own
  // openViewer) — just an anchor for the fallback fetch below, not
  // necessarily "the" study.
  const passedSeries = (location.state as { series?: ImagingSeries } | null)?.series;
  const passedSeriesMatches = passedSeries?.files.some((m) => m.id === studyId) ?? false;

  const [series, setSeries] = useState<ImagingSeries | null>(passedSeriesMatches ? passedSeries! : null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!passedSeriesMatches);

  // Falls back to fetching the single anchor file by id for a direct URL
  // visit, a refresh, or back/forward navigation where router state doesn't
  // survive — wrapped into a synthetic one-member "series" so DicomViewer's
  // props stay uniform either way. This degrades a multi-file series viewed
  // this way down to just its anchor member (no way to recover full series
  // grouping from a single id alone without a dedicated by-member-id lookup
  // endpoint, which doesn't exist yet) — acceptable since the normal click
  // path (Recent DICOM Uploads / the imaging list) always carries the full
  // series via router state and never hits this fallback.
  useEffect(() => {
    if (passedSeriesMatches) return;
    if (!studyId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getImagingStudy(studyId, actingAsProfileId)
      .then((result) => {
        if (cancelled) return;
        setSeries({
          seriesInstanceUid: result.seriesInstanceUid,
          studyInstanceUid: result.studyInstanceUid,
          modality: result.modality,
          title: result.title,
          createdAt: result.createdAt,
          fileCount: 1,
          frameCount: result.numberOfFrames && result.numberOfFrames > 0 ? result.numberOfFrames : 1,
          // ImagingSeriesMember is a full ImagingStudy (same shape
          // getImagingStudy already returns) — reuse it directly rather
          // than reconstructing a trimmed subset.
          files: [result],
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ImagingStudiesApiError ? err.message : "Couldn't load this imaging study.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId]);

  // Always back to Diagnostics & Imaging specifically — not a generic
  // browser-style navigate(-1) — since this page only ever exists as "that
  // page, showing one study instead of the list."
  const handleBack = () => navigate(DIAGNOSTICS_PATH);

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="mb-5 flex flex-wrap items-start gap-4">
        <PageBackButton onClick={handleBack} ariaLabel="Back to Diagnostics & Imaging" className="!mt-1" />
        <div className="min-w-0">
          <p className="truncate text-lg font-extrabold text-[#0A2947]">
            {series?.title || series?.files[0]?.originalFilename || "DICOM Viewer"}
          </p>
          {series && (
            <p className="truncate text-sm text-[#9CA3AF]">
              {series.modality ? `${series.modality} · ` : ""}
              {series.fileCount > 1 ? `${series.fileCount} files` : series.files[0]?.originalFilename}
            </p>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-[420px] flex-col items-center justify-center gap-3 text-[#8A8A8A]">
          <Spinner />
          <p className="text-xs font-medium">Loading imaging study…</p>
        </div>
      ) : error || !series ? (
        <div className="flex h-[420px] flex-col items-center justify-center gap-3 text-center text-[#0A2947]">
          <Icon icon="lucide:file-x" className="h-8 w-8 text-[#F17893]" />
          <p className="text-sm font-bold">Couldn&apos;t load this imaging study</p>
          <p className="max-w-sm text-xs text-[#8A8A8A]">{error || "This study may have been deleted."}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="!mt-1 !h-8 !rounded-full !text-xs !font-semibold"
          >
            Go back
          </Button>
        </div>
      ) : (
        // A fixed generous height (rather than filling the whole viewport,
        // since this page keeps the normal sidebar/header — see DicomViewer's
        // own docstring for why it fills whatever height its parent gives
        // it) — big enough to show the image far larger than the old modal
        // ever did, while still fitting comfortably alongside the app's
        // usual chrome. 190px accounts for PatientHeader (73px) plus this
        // page's own back-button row and padding — tuned to leave the same
        // small margin below the canvas that it already has on either side,
        // rather than a large empty gap.
        <div className="h-[calc(100vh-190px)] min-h-[420px]">
          <DicomViewer
            // NOT series.seriesInstanceUid — two distinct series can share
            // that raw tag (see getSeriesGroupId's own docstring); this key
            // exists specifically to force DicomViewer to remount (discard
            // its internal viewport/frame-index/annotation state) whenever
            // the OPENED series genuinely changes, so it must be an
            // identifier that's actually unique per series.
            key={getSeriesGroupId(series)}
            series={series}
            actingAsProfileId={actingAsProfileId}
            getCachedFileBlob={getCachedFileBlob}
            cacheFileBlob={cacheFileBlob}
          />
        </div>
      )}
    </div>
  );
}
